import * as React from 'react';
import {
  tk,
  ToggleGroup,
  FormField,
  FormFieldLabel,
  FormFieldHint,
  TextField,
  FormFieldControl,
  FormFieldMessageContainer,
  SpaceBetween,
  PhoneField,
  countryData,
  FormFieldError,
  ButtonLoaderIcon,
  Button,
} from '@sunwebgroup/ui-components';
import type { SubmitHandler, UseFormRegister, FieldErrors } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { styled } from 'styled-components';
import { useIntl } from '@/intl';
import { useSetParticipantGroupForm } from '@/domain/groups/hooks';
import type { PersonalDataFormInput } from '@/domain/groups/types';
import { titleRadioGroupOptions } from '@/domain/groups/types';
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks';

interface RenderTextFieldProps {
  label: string;
  name: keyof PersonalDataFormInput;
  register: UseFormRegister<PersonalDataFormInput>;
  errors: FieldErrors<PersonalDataFormInput>;
  requiredMessage: string;
  hintMessage?: string;
  type?: string;
  validate?: (value: string) => boolean | string;
  validationMessage?: string;
  pattern?: { value: RegExp; message: string };
  wrapper?: React.ComponentType<{ children: React.ReactNode }>;
  isDisabled?: boolean;
}

const validateAge = (value: string) => {
  if (!value) {
    return false;
  }
  const today = new Date();
  const birthDate = new Date(value);
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    return age - 1 >= 18;
  }
  return age >= 18;
};

export function PersonalDetailsForm() {
  const bookingDraftQueryResult = useBookingDraftQuery();
  const { setParticipantsForm } = useSetParticipantGroupForm(bookingDraftQueryResult);
  const { data } = useBookingDraftQuery();
  const participants = data?.participants ?? {};
  const mainParticipant = Object.values(participants ?? {})[0];
  const defaultCountry = countryData.find(({ isoCode }) => isoCode === 'NL') ?? countryData[0];

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitSuccessful },
  } = useForm<PersonalDataFormInput>({
    defaultValues: {
      title: (mainParticipant?.title?.toLowerCase() as 'mr' | 'ms' | undefined) ?? undefined,
      firstName: mainParticipant?.name ?? '',
      lastName: mainParticipant?.surname ?? '',
      birthDate: mainParticipant?.birthdate ?? '',
      mobilePhoneNumber: mainParticipant?.mobilePhoneNumber ?? { phoneNumber: '', countryCode: defaultCountry.code },
      emergencyPhoneNumber: mainParticipant?.emergencyPhoneNumber ?? {
        phoneNumber: '',
        countryCode: defaultCountry.code,
      },
      email: mainParticipant?.email ?? '',
    },
  });

  const [selectedCountryMobile, setSelectedCountryMobile] = React.useState(
    countryData.find(({ code }) => code === mainParticipant?.mobilePhoneNumber?.countryCode) ?? defaultCountry
  );
  const [selectedCountryEmergencyPhone, setSelectedCountryEmergencyPhone] = React.useState(
    countryData.find(({ code }) => code === mainParticipant?.emergencyPhoneNumber?.countryCode) ?? defaultCountry
  );

  const intl = useIntl();

  const nameValidationPattern = {
    value: /^(?: {0,}[A-Za-zÆæØøÅå'ÄäÖöÜüßÉéÀàÈèÙùÂâÊêÎîÔôÛûËëÏïŸÿÇçÁáÍíÓóÚú-]{2,} {0,}){1,}$/,
    message: intl.formatMessage({ id: 'groups_personal_data_step_field_name_validate' }),
  };

  const onSubmit: SubmitHandler<PersonalDataFormInput> = (result: PersonalDataFormInput) => {
    setParticipantsForm(result);
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <SpaceBetween direction="y" spacing={tk('spacing_lg')} alignItems="center">
        <GroupStepContainer direction="y" spacing={tk('spacing_sm')}>
          <FormField $horizontalLabel as="div">
            <FormFieldLabel>{intl.formatMessage({ id: 'groups_personal_data_step_field_title_label' })}</FormFieldLabel>
            <FormFieldControl>
              <Controller
                render={({ field: { value, onChange } }) => {
                  return (
                    <ToggleGroup type="single" value={value} onValueChange={onChange}>
                      {titleRadioGroupOptions.map((option) => (
                        <ToggleGroup.Item key={option} value={option}>
                          {intl.formatMessage({ id: `groups_personal_data_step_field_title_${option}_text` })}
                        </ToggleGroup.Item>
                      ))}
                    </ToggleGroup>
                  );
                }}
                name="title"
                control={control}
                rules={{
                  required: intl.formatMessage({ id: 'groups_personal_data_step_field_title_required' }),
                }}
                disabled={isSubmitSuccessful}
              />
            </FormFieldControl>
            {errors.title && (
              <FormFieldMessageContainer>
                <FormFieldError>{errors.title.message}</FormFieldError>
              </FormFieldMessageContainer>
            )}
          </FormField>
          {renderTextField({
            label: intl.formatMessage({ id: 'groups_personal_data_step_field_first_name_label' }),
            name: 'firstName',
            register,
            errors,
            hintMessage: intl.formatMessage({ id: 'groups_personal_data_step_field_first_name_hint' }),
            requiredMessage: intl.formatMessage({ id: 'groups_personal_data_step_field_first_name_required' }),
            pattern: nameValidationPattern,
            isDisabled: isSubmitSuccessful,
          })}
          {renderTextField({
            label: intl.formatMessage({ id: 'groups_personal_data_step_field_last_name_label' }),
            name: 'lastName',
            register,
            errors,
            hintMessage: intl.formatMessage({ id: 'groups_personal_data_step_field_last_name_hint' }),
            requiredMessage: intl.formatMessage({ id: 'groups_personal_data_step_field_last_name_required' }),
            pattern: nameValidationPattern,
            isDisabled: isSubmitSuccessful,
          })}
          {renderTextField({
            label: intl.formatMessage({ id: 'groups_personal_data_step_field_birth_date_label' }),
            name: 'birthDate',
            register,
            errors,
            type: 'date',
            requiredMessage: intl.formatMessage({ id: 'groups_personal_data_step_field_birth_date_required' }),
            wrapper: SmallTextFieldContent,
            isDisabled: isSubmitSuccessful,
            validate: validateAge,
            validationMessage: intl.formatMessage({ id: 'groups_personal_data_step_field_birth_date_valid_age' }),
          })}
          <Controller
            name="mobilePhoneNumber"
            control={control}
            rules={{
              required: intl.formatMessage({ id: 'groups_personal_data_step_field_mobile_phone_number_required' }),
              validate: (value) => {
                const phoneNumberWithoutPrefix = value?.phoneNumber;
                if (!phoneNumberWithoutPrefix) {
                  return intl.formatMessage({ id: 'groups_personal_data_step_field_mobile_phone_number_required' });
                }
                const phonePattern = new RegExp(selectedCountryMobile.pattern);
                return (
                  phonePattern.test(phoneNumberWithoutPrefix) ||
                  intl.formatMessage({ id: 'groups_personal_data_step_field_mobile_phone_number_validate' })
                );
              },
            }}
            render={({ field: { onChange, value } }) => (
              <PhoneField
                label={intl.formatMessage({ id: 'groups_personal_data_step_field_mobile_phone_number_label' })}
                countries={countryData}
                id="mobile-phone-number"
                value={value?.phoneNumber ?? ''}
                countryCode={value?.countryCode ?? selectedCountryMobile.code}
                onChange={onChange}
                onCountryChange={(country) => {
                  setSelectedCountryMobile(country);
                }}
                $disabled={isSubmitSuccessful}
                errorMessage={errors.mobilePhoneNumber?.message}
                $horizontalLabel
                hintMessage={intl.formatMessage({ id: 'groups_personal_data_step_field_mobile_phone_number_hint' })}
              />
            )}
          />

          <Controller
            name="emergencyPhoneNumber"
            control={control}
            rules={{
              required: intl.formatMessage({ id: 'groups_personal_data_step_field_emergency_phone_number_required' }),
              validate: (value) => {
                const emergencyPhoneNumberWithoutPrefix = value?.phoneNumber;
                if (!emergencyPhoneNumberWithoutPrefix) {
                  return intl.formatMessage({ id: 'groups_personal_data_step_field_emergency_phone_number_required' });
                }
                const phonePattern = new RegExp(selectedCountryEmergencyPhone.pattern);
                return (
                  phonePattern.test(emergencyPhoneNumberWithoutPrefix) ||
                  intl.formatMessage({ id: 'groups_personal_data_step_field_emergency_phone_number_validate' })
                );
              },
            }}
            render={({ field: { onChange, value } }) => (
              <PhoneField
                label={intl.formatMessage({ id: 'groups_personal_data_step_field_emergency_phone_number_label' })}
                countries={countryData}
                id="emergency-phone-number"
                value={value?.phoneNumber ?? ''}
                countryCode={value?.countryCode ?? selectedCountryEmergencyPhone.code}
                onChange={onChange}
                onCountryChange={(country) => {
                  setSelectedCountryEmergencyPhone(country);
                }}
                $disabled={isSubmitSuccessful}
                errorMessage={errors.emergencyPhoneNumber?.message}
                $horizontalLabel
              />
            )}
          />

          {renderTextField({
            label: intl.formatMessage({ id: 'email_address' }),
            name: 'email',
            register,
            errors,
            requiredMessage: intl.formatMessage({ id: 'groups_personal_data_step_field_email_required' }),
            pattern: {
              value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
              message: intl.formatMessage({ id: 'groups_personal_data_step_field_email_validate' }),
            },
            hintMessage: intl.formatMessage({ id: 'groups_personal_data_step_field_email_hint' }),
            isDisabled: isSubmitSuccessful,
          })}
        </GroupStepContainer>
        <Button type="submit" disabled={isSubmitSuccessful}>
          {isSubmitSuccessful && <ButtonLoaderIcon />}
          {intl.formatMessage({ id: 'groups_personal_data_step_next_button' })}
        </Button>
      </SpaceBetween>
    </Form>
  );
}

const renderTextField = ({
  label,
  name,
  register,
  errors,
  requiredMessage,
  hintMessage,
  type = 'text',
  pattern,
  validate,
  validationMessage,
  wrapper: Wrapper = FormFieldControl,
  isDisabled: isSubmitSuccessful,
}: RenderTextFieldProps) => (
  <FormField $horizontalLabel>
    <FormFieldLabel>{label}</FormFieldLabel>
    <Wrapper>
      <TextField.Root error={Boolean(errors[name])} disabled={isSubmitSuccessful}>
        <TextField.Content>
          <TextField.Input
            type={type}
            {...register(name, {
              required: requiredMessage,
              pattern,
              validate: (value: unknown) => {
                if (typeof value !== 'string') {
                  return validationMessage || 'Invalid input';
                }
                if (!validate) {
                  return true;
                }
                return validate(value) || validationMessage;
              },
            })}
          />
        </TextField.Content>
      </TextField.Root>
    </Wrapper>
    <FormFieldMessageContainer>
      {errors[name] ? (
        <FormFieldError>{errors[name]?.message}</FormFieldError>
      ) : (
        <FormFieldHint>{hintMessage}</FormFieldHint>
      )}
    </FormFieldMessageContainer>
  </FormField>
);

const GroupStepContainer = styled(SpaceBetween)`
  background: ${tk('color_semantic_bg')};
  border-radius: ${tk('radius_large')};
  padding: ${tk('spacing_lg')};
  width: 100%;

  ${FormFieldLabel} {
    @media ${tk('mq_tablet')} {
      margin-left: 50px;
      width: 150px;
    }
  }
`;

const SmallTextFieldContent = styled(FormFieldControl)`
  width: 200px;

  > * {
    width: 100%;
  }
`;

const Form = styled.form``;
